import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { formatDateView } from './utils/dateUtils';
import { formatString, truncateText } from './utils/StringUtils';
import { formatCurrency } from './utils/formatMoeda';
import Pagination10 from './components/Pagination10';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import LoadingModal from './components/modals/LoadingModal';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import DemandaModal from './components/modals/DemandaModal';
import DemandaItensModal from './components/modals/DemandaItensModal';
import DemandaLotesModal from './components/modals/DemandaLotesModal';
import DataTable from './components/DataTable';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';

function Demandas() {
  const [demandas, setDemandas] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [selectedDemanda, setSelectedDemanda] = useState(null);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = localStorage.getItem('anoBase');
  const navigate = useNavigate();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const [showDemandaModal, setShowDemandaModal] = useState(false);
  const [showDemandaItensModal, setShowDemandaItensModal] = useState(false);
  const [showDemandaLotesModal, setShowDemandaLotesModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [editingDemanda, setEditingDemanda] = useState(null);
  // O valor de agrupamento agora vem do selectedDemanda, se existir
  const agrupamento = selectedDemanda?.agrupamento || 'ITEM';

  const totalItems = demandas.length;
  const { setPagination } = usePagination10(totalItems);

  const selectedDemandas = demandas.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  // Limpar seleção quando o filtro ou paginação mudar
  useEffect(() => {
    if (
      selectedDemanda &&
      !selectedDemandas.find(demanda => demanda.num === selectedDemanda.num)
    ) {
      setSelectedDemanda(null);
    }
  }, [selectedDemandas, selectedDemanda]);

  // Removido radio, seleção agora é feita por clique na linha

  const handleRowClick = demanda => {
    setSelectedDemanda(
      selectedDemanda && selectedDemanda.num === demanda.num ? null : demanda
    );
  };

  const handleCreateDemanda = () => {
    setEditingDemanda(null);
    setShowDemandaModal(true);
  };

  const handleEditDemanda = () => {
    if (selectedDemanda) {
      setEditingDemanda(selectedDemanda);
      setShowDemandaModal(true);
    }
  };

  const handleDemandaSuccess = () => {
    setShowDemandaModal(false);
    setEditingDemanda(null);
    // Recarregar a lista de demandas
    axios
      .get(`${API_BASE_URL.DEMANDA}/cnpj`, {
        params: {
          cnpj: cnpj,
          anoBase: anoBase,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setDemandas(result.data);
        setDefaultModal({
          title: 'Sucesso',
          message: editingDemanda ? 'Demanda atualizada com sucesso!' : 'Demanda criada com sucesso!',
          variant: 'success',
        });
      })
      .catch(error => {
        console.error('Erro ao recarregar demandas:', error);
      });
  };

  const handleDemandaClose = () => {
    setShowDemandaModal(false);
    setEditingDemanda(null);
  };

  // Funcionalidade de teclado simplificada
  useEffect(() => {
    const handleKeyDown = event => {
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.tagName === 'SELECT'
      ) {
        return; // Não interceptar se estiver em um campo de entrada
      }

      switch (event.key) {
        case 'Escape':
          setSelectedDemanda(null);
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (selectedDemanda) {
            const currentIndex = selectedDemandas.findIndex(
              demanda => demanda.num === selectedDemanda.num
            );
            if (currentIndex < selectedDemandas.length - 1) {
              setSelectedDemanda(selectedDemandas[currentIndex + 1]);
            }
          } else if (selectedDemandas.length > 0) {
            setSelectedDemanda(selectedDemandas[0]);
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (selectedDemanda) {
            const currentIndex = selectedDemandas.findIndex(
              demanda => demanda.num === selectedDemanda.num
            );
            if (currentIndex > 0) {
              setSelectedDemanda(selectedDemandas[currentIndex - 1]);
            }
          } else if (selectedDemandas.length > 0) {
            setSelectedDemanda(selectedDemandas[selectedDemandas.length - 1]);
          }
          break;
        case ' ':
          event.preventDefault();
          if (selectedDemanda) {
            // Para radio, espaço apenas confirma a seleção atual
            event.preventDefault();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedDemanda, selectedDemandas]);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.DEMANDA}/cnpj`, {
        params: {
          cnpj: cnpj,
          anoBase: anoBase,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setDemandas(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter listas:', error);
        navigate('/');
      });
    setLoading(false);
  }, [refreshTrigger]);

  if (!demandas) {
    return <div>Loading...</div>;
  }

  function handleInformation(demandaToAprove) {
    axios
      .get(`${API_BASE_URL.TRAMITACAO}/justificativaDevolucao`, {
        params: {
          cnpjEntidade: demandaToAprove.cnpj,
          demandaNum: demandaToAprove.num,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        if (result.data) {
          const justificativa = result.data.justificativa;
          showDefaultModal(
            'information',
            `**Justificativa da devolução:**\n ${justificativa}`
          );
        } else {
        }
      })
      .catch(error => {
        console.error('Erro ao obter justificativa:', error);
        showDefaultModal('error', 'Erro ao obter justificativa!');
      });
  }
  function handleSend(demandaToAprove) {
    showConfirmModal('sendAprovacao', () => handleSendConfirm(demandaToAprove));
  }

  function handleSendConfirm(demandaToAprove) {
    if (demandaToAprove) {
      axios
        .get(`${API_BASE_URL.DEMANDA_ITEM}/validate`, {
          params: {
            cnpj: demandaToAprove.cnpj,
            num: demandaToAprove.num,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          if (result.data) {
            if (
              !demandaToAprove.dataCriacao ||
              !demandaToAprove.dataConclusao ||
              !demandaToAprove.tipo ||
              !demandaToAprove.requisitante ||
              !demandaToAprove.objeto ||
              !demandaToAprove.tipoDemanda ||
              !demandaToAprove.regulamentacao
            ) {
              const missingFields = [];
              if (!demandaToAprove.dataCriacao)
                missingFields.push('Data de Criação');
              if (!demandaToAprove.dataConclusao)
                missingFields.push('Data de Conclusão');
              if (!demandaToAprove.tipo) missingFields.push('Tipo');
              if (!demandaToAprove.requisitante)
                missingFields.push('Requisitante');
              if (!demandaToAprove.objeto) missingFields.push('Objeto');
              if (!demandaToAprove.tipoDemanda)
                missingFields.push('Tipo de Demanda');
              if (!demandaToAprove.regulamentacao)
                missingFields.push('Regulamentação Municipal');

              const plural = missingFields.length > 1 ? 'os campos' : 'o campo';
              const fields = missingFields.join(', ');

              showDefaultModal(
                'error',
                `Por favor, preencha ${plural}: ${fields}.`
              );
              return;
            }
            const updatedFormData = {
              ...demandaToAprove,
              tramitacao: 'ENVIADO PARA APROVAÇÃO',
            };
            axios
              .post(
                `${API_BASE_URL.DEMANDA}/updateTramitacao`,
                updatedFormData,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              )
              .then(result => {
                setDemandas(prevState =>
                  prevState.map(item =>
                    item.num === demandaToAprove.num
                      ? { ...item, tramitacao: 'ENVIADO PARA APROVAÇÃO' }
                      : item
                  )
                );
                showDefaultModal(
                  'success',
                  'O registro foi enviado com sucesso'
                );
              })
              .catch(error => {
                console.error(
                  'Erro ao enviar o registro para aprovação:',
                  error
                );
                showDefaultModal(
                  'error',
                  'Erro ao enviar o registro para aprovação!'
                );
              });
          } else {
            showDefaultModal(
              'error',
              'Não é possível enviar para aprovação. Existem itens que precisam de atenção como categoria, unidade, quantidade e valor unitário!'
            );
          }
        })
        .catch(error => {
          console.error(
            'Não é possível enviar para aprovação. Existem itens que precisam de atenção como categoria, unidade, quantidade e valor unitário:',
            error
          );
          showDefaultModal(
            'error',
            'Não é possível enviar para aprovação. Existem itens que precisam de atenção como categoria, unidade, quantidade e valor unitário!'
          );
        });
    }
  }

  function handleDelete(demandaToDelete) {
    showConfirmModal('delete', () => handleDeleteConfirm(demandaToDelete));
  }

  function handleDeleteConfirm(demandaToDelete) {
    if (demandaToDelete) {
      axios
        .get(`${API_BASE_URL.DEMANDA_ITEM}/exists`, {
          params: { cnpj: demandaToDelete.cnpj, num: demandaToDelete.num },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(result => {
          if (result.data.exists) {
            showDefaultModal(
              'error',
              'Este registro possui vínculos e não pode ser excluído. Exclua os vínculos e tente novamente.'
            );
          } else {
            axios
              .delete(`${API_BASE_URL.DEMANDA}/delete`, {
                params: {
                  cnpj: demandaToDelete.cnpj,
                  num: demandaToDelete.num,
                },
                headers: { Authorization: `Bearer ${token}` },
              })
              .then(result => {
                setDemandas(prevState =>
                  prevState.filter(item => item.num !== demandaToDelete.num)
                );
                showDefaultModal(
                  'success',
                  'O registro foi excluído com sucesso'
                );
              })
              .catch(error => {
                console.error('Erro ao excluir o item:', error);
                showDefaultModal('error', 'Erro ao excluir o registro!');
              });
          }
        });
    }
  }

  async function handlePrint(demanda) {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL.DEMANDA}/preencherDemanda`,
        demanda,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `DEMANDA ${formatString(demanda.num, 3)}.docx`;
      link.click();
    } catch (error) {
      console.error('Erro ao preencher o template:', error);
      showDefaultModal('error', 'Erro ao preencher o template.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Demandas Cadastradas" icon="bi-file-earmark-text" />
        <PageContent>
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-success btn-sm d-inline-flex align-items-center"
                onClick={handleCreateDemanda}
                title="Criar nova Demanda"
              >
                <i className="bi bi-plus-circle me-1"></i>Novo
              </button>
              <button
                className="btn btn-primary btn-sm"
                disabled={!selectedDemanda}
                onClick={handleEditDemanda}
                title="Editar demanda selecionada"
              >
                <i className="bi bi-pencil-square me-1"></i>Editar
              </button>
              
              <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(selectedDemanda)}
                    title="Excluir demanda"
                    disabled={!selectedDemanda}
                  >
                    <i className="bi bi-trash3 me-1"></i>Excluir
                  </button>
              {/* <button
                className="btn btn-secondary btn-sm"
                disabled={!selectedDemanda}
                onClick={() => selectedDemanda && handlePrint(selectedDemanda)}
                title="Imprimir demanda"
              >
                <i className="bi bi-printer me-1"></i>Imprimir
              </button> */}
              {selectedDemanda &&
                selectedDemanda.tramitacao === 'DEVOLVIDO PARA AJUSTES' && (
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => handleInformation(selectedDemanda)}
                    title="Ver justificativa da devolução"
                  >
                    <i className="bi bi-info-circle me-1"></i>Justificativa
                  </button>
                )}
              {selectedDemanda &&
                selectedDemanda.tramitacao === 'EM ELABORAÇÃO' && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(selectedDemanda)}
                    title="Excluir demanda"
                  >
                    <i className="bi bi-trash3 me-1"></i>Excluir
                  </button>
                )}
            </div>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              {selectedDemanda &&
                (selectedDemanda.tramitacao === 'EM ELABORAÇÃO' ||
                  selectedDemanda.tramitacao === 'DEVOLVIDO PARA AJUSTES') && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleSend(selectedDemanda)}
                    title="Enviar para aprovação"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <i className="bi bi-send me-1"></i>Enviar para Aprovação
                  </button>
                )}
                <button
                className="btn btn-info btn-sm"
                disabled={!selectedDemanda}
                onClick={() => {
                  if (!selectedDemanda) return;
                  if (agrupamento === 'LOTE') {
                    setShowDemandaLotesModal(true);
                  } else {
                    setShowDemandaItensModal(true);
                  }
                }}
                title={
                  agrupamento === 'LOTE'
                    ? 'Ver lotes da demanda'
                    : 'Ver itens da demanda'
                }
              >
                <i
                  className={`bi ${
                    agrupamento === 'LOTE' ? 'bi-boxes' : 'bi-list-ul'
                  } me-1`}
                ></i>
                {agrupamento === 'LOTE' ? 'Lotes' : 'Itens'}
              </button>

            </div>
          </div>
          <TableContainer isLoading={isLoading}>
              <DataTable style={{ fontSize: '0.8rem' }} head={
                <tr>
                  <th
                    scope="col"
                    className="align-middle"
                    style={{ minWidth: '60px' }}
                  >
                    Nº
                  </th>
                  <th
                    scope="col"
                    className="align-middle"
                    style={{ minWidth: '110px' }}
                  >
                    Data de Criação
                  </th>
                  <th
                    scope="col"
                    className="align-middle"
                    style={{ minWidth: '100px' }}
                  >
                    Tipo
                  </th>
                  <th
                    scope="col"
                    className="align-middle"
                    style={{ minWidth: '120px' }}
                  >
                    Agrupamento
                  </th>
                  <th scope="col" className="align-middle" style={{ minWidth: '200px' }}>
                    Objeto
                  </th>
                  <th
                    scope="col"
                    className="align-middle text-center"
                    style={{ minWidth: '100px' }}
                  >
                    Qtd.
                  </th>
                  <th
                    scope="col"
                    className="align-middle text-end"
                    style={{ minWidth: '120px' }}
                  >
                    Valor Estimado
                  </th>
                </tr>
              }>
              <tbody>
                {!isLoading && selectedDemandas.length > 0 ? (
                  selectedDemandas.map(c => {
                    const isActiveRow =
                      selectedDemanda && selectedDemanda.num === c.num;
                    return (
                      <tr
                        key={`${c.cnpj}-${c.num}`}
                        className={isActiveRow ? 'table-primary fw-bold' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleRowClick(c)}
                        title={
                          isActiveRow
                            ? 'Demanda selecionada'
                            : 'Clique para selecionar'
                        }
                      >
                        <td>
                          {formatString(c.num, 4)}
                        </td>
                        <td>
                          {formatDateView(c.dataCriacao)}
                        </td>
                        <td>{c.tipo}</td>
                        <td>{c.agrupamento}</td>
                        <td>{truncateText(c.objeto, 160)}</td>
                        <td className="text-center">
                          {c.agrupamento === 'LOTE' 
                            ? `${c.qtdLotes || 0} lote${(c.qtdLotes || 0) !== 1 ? 's' : ''} / ${c.qtdItens || 0} ${(c.qtdItens || 0) !== 1 ? 'itens' : 'item'}`
                            : `${c.qtdItens || 0} ${(c.qtdItens || 0) !== 1 ? 'itens' : 'item'}`
                          }
                        </td>
                        <td className="text-end">
                          {formatCurrency(c.valorEstimado || 0)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      Nenhum registro encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
              </DataTable>
            </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination}/>
        <DefaultModal {...setDefaultModal} />
        <ConfirmModal {...setConfirmModal} />
        <DemandaModal
          show={showDemandaModal}
          onClose={handleDemandaClose}
          onSuccess={handleDemandaSuccess}
          demandaData={editingDemanda}
        />
        <DemandaItensModal
          show={showDemandaItensModal}
          onClose={() => {
            setShowDemandaItensModal(false);
            setRefreshTrigger(prev => prev + 1);
          }}
          demanda={selectedDemanda}
        />
        <DemandaLotesModal
          show={showDemandaLotesModal}
          onClose={() => {
            setShowDemandaLotesModal(false);
            setRefreshTrigger(prev => prev + 1);
          }}
          demanda={selectedDemanda}
        />

      </ResponsiveShadow>
    </Layout>
  );
}

export default Demandas;
